import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * Where a softphone device runs (PD-14897). Derived by the browser from
 * its user agent and, inside the native app, the Capacitor platform.
 */
export type SoftphoneDevicePlatformModel = 'web' | 'desktop' | 'ios' | 'android';

/**
 * One row per device a rep uses the softphone on (PD-14897).
 *
 * The Twilio identity is per USER (`user-{alias}__{userId}`), so Twilio
 * cannot tell a rep's laptop from their phone. This registry is the
 * server-side layer that can: it lets one device see that a call is in
 * progress on another, and it gives each call leg an owning device so
 * ownership survives a page reload on that device.
 *
 * A "device" is one browser profile (or one install of the native app):
 * the browser generates `deviceKey` once and keeps it in localStorage, so
 * every tab of the same profile is the same device. Only the tab that
 * holds the softphone leader lock registers and heartbeats.
 *
 * Online is computed on read from `lastSeenAt` (heartbeat every 30 s,
 * online while seen within the last 90 s), so there is no sweeper and no
 * stale "online" flag to clear. `signedOutAt` is set when the rep signs
 * out on that device; a later heartbeat clears it.
 *
 * Not auditable: a heartbeat rewrites `lastSeenAt` twice a minute per
 * device. The row is presence telemetry, not business data, and mirroring
 * it into `_audit` would add the highest-frequency write in the softphone
 * for no reader.
 */
@Index('softphone_device_user_key_uidx', ['fkUserId', 'deviceKey'], {
	unique: true,
})
@Entity('softphone_device', {}, { auditable: false })
export class SoftphoneDeviceEntity extends BaseEntity {
	/** Tenant-local `user._id` of the rep who owns the device. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/** Random id the browser generated for this browser profile / app
	 *  install. Unique per user, not globally. */
	@Column('varchar', { name: 'deviceKey', length: 64 })
	public deviceKey!: string;

	/** Human label shown on the rep's other devices, e.g. "Chrome on Mac". */
	@Column('varchar', { name: 'label', length: 128 })
	public label!: string;

	@Column('varchar', { name: 'platform', length: 16 })
	public platform!: SoftphoneDevicePlatformModel;

	@Column('varchar', { name: 'userAgent', length: 512, nullable: true })
	public userAgent!: string | null;

	/** What the client says it can do (for example whether it can place a
	 *  claim leg). Free-form, informational. */
	@Column('json', { name: 'capabilities', nullable: true })
	public capabilities!: Record<string, unknown> | null;

	@Column('datetime', { name: 'lastSeenAt' })
	public lastSeenAt!: Date;

	@Column('datetime', { name: 'signedOutAt', nullable: true })
	public signedOutAt!: Date | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
